import { ApiError, api, setCsrfToken } from "@/shared/api/client";
import { MeResponse, type UserSummary } from "@/shared/api/schemas";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const ME_KEY = ["auth", "me"] as const;

export const meQuery = queryOptions({
  queryKey: ME_KEY,
  queryFn: async (): Promise<UserSummary | null> => {
    try {
      const raw = await api<unknown>("/v1/auth/me");
      const parsed = MeResponse.parse(raw);
      setCsrfToken(parsed.csrfToken ?? null);
      return parsed.user;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 60_000,
});

export function useSession() {
  return useQuery(meQuery);
}

/** Full-page navigation into the OIDC flow (authentik). The API redirects back. */
export function startLogin(returnTo = "/"): void {
  window.location.assign(`/v1/auth/oidc/login?returnTo=${encodeURIComponent(returnTo)}`);
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api<null>("/v1/auth/logout", { method: "POST" });
    },
    onSettled: () => {
      setCsrfToken(null);
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });
}
